import api from './api.service';
import type { ApiResponse } from '@/types';

/** React Query root key; invalidate with `['mfg-execution-board']` prefix. */
export const MFG_EXECUTION_BOARD_QUERY_KEY = 'mfg-execution-board' as const;

export interface StageQueueStageActivityEntry {
  event_type: string;
  occurred_at: string;
  user_id: string | null;
  notes: string | null;
}

export interface StageQueueStageRow {
  stage_id: string;
  status: string;
  sequence_number: number;
  /** BOQ estimation operation sequence when linked; prefer for operator-facing display. */
  boq_operation_sequence?: number | null;
  stage_code: string;
  stage_name: string;
  work_order_id: string;
  work_order_line_item_id: string | null;
  line_number: number | null;
  line_item_code: string | null;
  line_item_description: string | null;
  /** Planned qty for the WO line item (not WO header total). */
  line_planned_quantity?: number | null;
  operation_label: string;
  started_date: string | null;
  completed_date: string | null;
  planned_start_date: string | null;
  planned_end_date: string | null;
  estimated_hours: number | null;
  /** 1-based production unit for BOQ-linked ops; null = legacy */
  production_unit_number?: number | null;
  assigned_to_user_id: string | null;
  operator_name: string | null;
  inspector_user_id: string | null;
  inspector_name: string | null;
  verifier_user_id: string | null;
  verifier_name: string | null;
  workstation_id: string | null;
  workstation_code: string | null;
  workstation_name: string | null;
  notes: string | null;
  is_overdue: boolean;
  can_start: boolean;
  can_resume: boolean;
  iir_approved: boolean;
  iir_status: string | null;
  /** When false, shop floor may start without completing the prior seq on this line (matches API). */
  wait_for_previous?: boolean;
  inspection_required?: boolean;
  inspection_status?: string | null;
  /** Present when API returns execution-board payload with events (after migration). */
  activity_log?: StageQueueStageActivityEntry[];
}

export interface StageQueueWorkOrderGroup {
  work_order_id: string;
  work_order_number: string;
  work_order_status: string;
  customer_name: string | null;
  sales_order_number: string | null;
  project_name: string | null;
  project_number: string | null;
  planned_end_date: string | null;
  produced_quantity: number;
  planned_quantity: number;
  item_code: string | null;
  item_name: string | null;
  production_supervisor_user_id: string | null;
  production_supervisor_name: string | null;
  quality_engineer_user_id: string | null;
  quality_engineer_name: string | null;
  iir_approved: boolean;
  iir_status: string | null;
  stages_total: number;
  stages_pending: number;
  stages_in_progress: number;
  stages_paused: number;
  stages_on_hold: number;
  stages_completed: number;
  stages_skipped: number;
  progress_percent: number;
  execution_scope?: string | null;
  stages: StageQueueStageRow[];
}

export interface StageQueueResult {
  work_orders: StageQueueWorkOrderGroup[];
  total_work_orders: number;
  page: number;
  page_size: number;
}

/** Serializable filters sent to the stage-queue API (also used for query key). */
export interface StageQueueFilters {
  page: number;
  page_size: number;
  include_completed_wo: boolean;
  hide_skipped: boolean;
  only_actionable: boolean;
  work_order_status?: string;
  stage_status?: string;
  customer_id?: string;
  project_id?: string;
  supervisor_user_id?: string;
  qe_user_id?: string;
  search?: string;
  due_before?: string;
  due_after?: string;
  /** Workstation UUID, or `__unassigned__` for no workstation. */
  workstation_id?: string;
}

export const defaultStageQueueFilters = (): StageQueueFilters => ({
  page: 1,
  page_size: 100,
  include_completed_wo: false,
  hide_skipped: true,
  only_actionable: false,
});

/**
 * Build query string for GET /manufacturing/execution/stage-queue (used by API + tests).
 */
export function buildStageQueueQueryString(filters: StageQueueFilters): string {
  const p = new URLSearchParams();
  p.set('page', String(filters.page));
  p.set('page_size', String(filters.page_size));
  if (filters.include_completed_wo) p.set('include_completed_wo', 'true');
  if (!filters.hide_skipped) p.set('hide_skipped', 'false');
  if (filters.only_actionable) p.set('only_actionable', 'true');
  if (filters.work_order_status) p.set('work_order_status', filters.work_order_status);
  if (filters.stage_status) p.set('stage_status', filters.stage_status);
  if (filters.customer_id) p.set('customer_id', filters.customer_id);
  if (filters.project_id) p.set('project_id', filters.project_id);
  if (filters.supervisor_user_id) p.set('supervisor_user_id', filters.supervisor_user_id);
  if (filters.qe_user_id) p.set('qe_user_id', filters.qe_user_id);
  if (filters.search?.trim()) p.set('search', filters.search.trim());
  if (filters.due_before) p.set('due_before', filters.due_before);
  if (filters.due_after) p.set('due_after', filters.due_after);
  if (filters.workstation_id) p.set('workstation_id', filters.workstation_id);
  return p.toString();
}

export async function fetchStageQueue(filters: StageQueueFilters): Promise<StageQueueResult> {
  const qs = buildStageQueueQueryString(filters);
  const res: ApiResponse<StageQueueResult> = await api.get(`/manufacturing/execution/stage-queue?${qs}`);
  if (!res.data) {
    return {
      work_orders: [],
      total_work_orders: 0,
      page: filters.page,
      page_size: filters.page_size,
    };
  }
  return res.data;
}
